import { baseUrl } from './api.js';
import { DomainApiError } from './api-error.js';
import { kodChyby } from './form-errors.js';

/**
 * Krok QAFIX2 — klient pruvodce „Prevod kancelare" (`/import`).
 *
 * Stejny vzor jako `assistant-settings-api.ts`: raw `fetch` s `credentials`,
 * protoze routy `/v1/office-migration/*` jeste nejsou v generovanem
 * `@reos/api-client` (openapi.json regeneruje CI).
 *
 * Soubor jde na server jako base64 v JSON tele — zadny multipart: API
 * `@fastify/multipart` nema a jedna cesta (JSON) staci. Strop drzi server
 * (`MAX_TABLE_BASE64`), klient ho hlasi dopredu, aby makler necekal na 413.
 */

/** Strop souboru, ktery server prijme (base64 je o tretinu delsi). */
export const MAX_IMPORT_FILE_BYTES = 5_500_000;

export interface ImportColumnMapping {
  readonly externalId: string;
  readonly title: string;
  readonly description?: string;
  readonly addressText?: string;
  readonly kind?: string;
  readonly photos?: string;
  readonly priceAmount?: string;
}

/** Pole mapovani, ktera obrazovka nabizi k rucnimu prirazeni sloupce. */
export const IMPORT_MAPPING_FIELDS = [
  'externalId',
  'title',
  'description',
  'addressText',
  'kind',
  'priceAmount',
  'photos',
] as const;

export type ImportMappingField = (typeof IMPORT_MAPPING_FIELDS)[number];

export interface ImportPreviewRow {
  readonly externalId: string;
  readonly title: string;
  readonly kind: string;
  readonly addressText: string | null;
  readonly description: string | null;
  readonly photos: readonly { readonly url: string }[];
  readonly facts: readonly { readonly factKey: string; readonly value: unknown }[];
}

export interface ImportTablePreview {
  readonly header: readonly string[];
  readonly totalRows: number;
  readonly suggestion: {
    readonly mapping: ImportColumnMapping | null;
    readonly unmapped: readonly string[];
    readonly missingRequired: readonly string[];
  };
  readonly mapping: ImportColumnMapping | null;
  readonly rows: readonly ImportPreviewRow[];
  readonly totalListings: number;
  readonly errors: readonly string[];
}

export interface ImportTableResult {
  readonly createdCases: number;
  readonly updatedCases: number;
  readonly skippedRows: number;
  readonly errors: readonly string[];
  readonly items: readonly { readonly externalId: string; readonly caseId: string; readonly created: boolean }[];
}

/** Bajty souboru jako base64 — bez `FileReader` (ten vraci data URL s prefixem). */
export async function fileToBase64(file: Blob): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  // Po blocich, aby `String.fromCharCode` nenarazil na strop argumentu.
  for (let offset = 0; offset < bytes.length; offset += 8192) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 8192));
  }
  return btoa(binary);
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${baseUrl}${path}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    // Stejny rozbal problem+json jako `acquisition-radar-api.ts`: `title` je
    // cesky text serveru, `detail` u 4xx duvod. `type` nese kod problemu,
    // podle ktereho obrazovka pozna stav „ceka na pristupy".
    const problem = (await res.json().catch(() => null)) as
      | { title?: string; detail?: string; type?: string }
      | null;
    const text = [problem?.title, problem?.detail]
      .filter((part): part is string => typeof part === 'string')
      .join(' ');
    throw new DomainApiError(text === '' ? `HTTP ${String(res.status)}` : text, res.status, problem?.type);
  }
  return (await res.json()) as T;
}

export async function previewImportTable(input: {
  readonly fileName: string;
  readonly contentBase64: string;
  readonly mapping?: ImportColumnMapping | undefined;
}): Promise<ImportTablePreview> {
  return post<ImportTablePreview>('/v1/office-migration/table/preview', {
    fileName: input.fileName,
    contentBase64: input.contentBase64,
    ...(input.mapping !== undefined ? { mapping: input.mapping } : {}),
    limit: 20,
  });
}

export async function importTable(input: {
  readonly fileName: string;
  readonly contentBase64: string;
  readonly mapping: ImportColumnMapping;
}): Promise<ImportTableResult> {
  return post<ImportTableResult>('/v1/office-migration/table/import', input);
}

/** Souhrn prevodu z uctu Sreality (jen cteni z portalu). */
export interface SrealityMigrationResult {
  readonly createdCases: number;
  readonly updatedCases: number;
  readonly notes: readonly string[];
  readonly items: readonly { readonly externalId: string; readonly caseId: string }[];
  readonly sellers: readonly { readonly name: string | null; readonly email: string | null }[];
}

export async function importFromSreality(): Promise<SrealityMigrationResult> {
  return post<SrealityMigrationResult>('/v1/office-migration/sreality/import', {});
}

/**
 * „Hotovo, ceka na pristupy k uctu kancelare" — server odpovida 409
 * `connector/connection-missing` nebo `connector/credentials-missing`
 * (vzor GCAL1/PORT1: chybejici pristupy NEJSOU chyba obrazovky).
 *
 * Kod se cte pres `kodChyby` — `ProblemError` posila `type` jako PLNOU URI
 * (`https://docs.reos.local/errors/connector/connection-missing`). Zive
 * overeni 20. 10. 2026 nad lokalnim API to ukazalo: porovnani s holym kodem
 * nesedlo a makler videl obecne „Konflikt stavu zdroje" misto sedeho stavu.
 */
const WAITING_FOR_ACCESS_CODES = ['connector/connection-missing', 'connector/credentials-missing'];

export function isWaitingForAccess(error: unknown): boolean {
  if (!(error instanceof DomainApiError) || error.type === undefined) return false;
  return error.status === 409 && WAITING_FOR_ACCESS_CODES.includes(kodChyby(error.type));
}

export function vendorSampleUrl(vendor: 'softreal' | 'realman'): string {
  return `${baseUrl}/v1/office-migration/table/sample?vendor=${vendor}`;
}
